import { DOCUMENT, DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { GA_MEASUREMENT_ID } from '../config';

type GtagFn = (...args: unknown[]) => void;

interface GtagWindow extends Window {
  dataLayer?: unknown[];
  gtag?: GtagFn;
}

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private readonly _document = inject(DOCUMENT);
  private readonly _router = inject(Router);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _measurementId = inject(GA_MEASUREMENT_ID);

  private _initialized = false;

  init(): void {
    if (this._initialized || !this._enabled) return;

    const win = this._window;
    if (!win) return;

    this._initialized = true;

    win.dataLayer = win.dataLayer || [];
    win.gtag =
      win.gtag ||
      function gtag(this: GtagWindow) {
        // gtag.js only consumes array-like `arguments` entries, not real Arrays.
        win.dataLayer?.push(arguments as unknown as unknown[]);
      };

    this._loadScript(win);
    this.gtag('js', new Date());
    this.gtag('config', this._measurementId, { send_page_view: false });

    this._router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((event) => this.trackPageView(event.urlAfterRedirects));
  }

  trackPageView(url: string): void {
    if (!this._enabled) return;

    this.gtag('event', 'page_view', { page_path: url });
  }

  private get _enabled(): boolean {
    return !!this._measurementId;
  }

  private get _window(): GtagWindow | null {
    return (this._document.defaultView as GtagWindow | null) ?? null;
  }

  private gtag(...args: unknown[]): void {
    this._window?.gtag?.(...args);
  }

  private _loadScript(win: GtagWindow): void {
    const script = this._document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this._measurementId}`;
    (this._document.head ?? win.document.head).appendChild(script);
  }
}
